import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../functions/authFetch";
import config from "../Config";

const BASE = () => `${config.MASTER_API || ""}/v1`;

const UsageBreakdown = () => {
  const navigate = useNavigate();

  const [allClusters, setAllClusters] = useState([]); // [{cluster_name, display_name, months}]
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetch_ = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authFetch(`${BASE()}/billing/usage-history`, { method: "GET" });
        if (cancelled) return;
        if (res.status === 401) { navigate("/login"); return; }
        if (res.ok) {
          const data = await res.json();
          setAllClusters(data.clusters || []);
          setSelectedIdx(0);
        } else {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || "Failed to load usage history.");
        }
      } catch { setError("Network error."); }
      finally { if (!cancelled) setLoading(false); }
    };
    fetch_();
    return () => { cancelled = true; };
  }, [navigate]);

  const cluster = allClusters[selectedIdx] || null;
  const months  = cluster?.months || [];

  const fmt = (v) => `$${Number(v || 0).toFixed(4)}`;
  const totalJob  = months.reduce((s, m) => s + (m.job_run    || 0), 0);
  const totalDepl = months.reduce((s, m) => s + (m.deployment || 0), 0);
  const totalAll  = totalJob + totalDepl;

  return (
    <section className="acct-card">
      <div className="acct-card-header">
        <h2>Usage Breakdown</h2>
      </div>

      <div className="acct-card-body">

        {loading && <p className="acct-empty">Loading…</p>}
        {error   && <p className="cl-error">{error}</p>}

        {!loading && !error && allClusters.length === 0 && (
          <p className="acct-empty">No usage recorded yet.</p>
        )}

        {!loading && !error && allClusters.length > 0 && (
          <>
            {/* Cluster selector */}
            <div className="ub-controls">
              <label className="ub-label">Cluster</label>
              <select
                className="ub-select"
                value={selectedIdx}
                onChange={e => setSelectedIdx(Number(e.target.value))}
              >
                {allClusters.map((c, i) => (
                  <option key={c.cluster_name} value={i}>{c.display_name}</option>
                ))}
              </select>
            </div>

            {months.length === 0 ? (
              <p className="acct-empty" style={{ marginTop: "1.25rem" }}>
                No usage recorded for this cluster yet.
              </p>
            ) : (
              <>
                {/* Table */}
                <div className="ub-table">
                  <div className="ub-row ub-head">
                    <span>Month</span>
                    <span className="ub-num">Job Runs</span>
                    <span className="ub-num">Deployments</span>
                    <span className="ub-num">Total</span>
                  </div>

                  {months.map((m) => (
                    <div key={m.month} className="ub-row">
                      <span className="ub-month">{m.month}</span>
                      <span className="ub-num">{fmt(m.job_run)}</span>
                      <span className="ub-num">{fmt(m.deployment)}</span>
                      <span className="ub-num ub-total">{fmt(m.total)}</span>
                    </div>
                  ))}

                  <div className="ub-row ub-foot">
                    <span>All time</span>
                    <span className="ub-num">{fmt(totalJob)}</span>
                    <span className="ub-num">{fmt(totalDepl)}</span>
                    <span className="ub-num ub-total">{fmt(totalAll)}</span>
                  </div>
                </div>

                {/* Bar chart */}
                <div className="ub-chart-section">
                  <p className="ub-chart-label">Monthly spend</p>
                  {months.map((m) => {
                    const pct    = totalAll > 0 ? ((m.total   / totalAll) * 100).toFixed(1) : 0;
                    const jobPct = m.total  > 0 ? ((m.job_run / m.total)  * 100).toFixed(1) : 0;
                    return (
                      <div key={m.month} className="ub-bar-row">
                        <span className="ub-bar-month">{m.month}</span>
                        <div className="ub-bar-track">
                          <div className="ub-bar-fill" style={{ width: `${pct}%` }}>
                            <div className="ub-bar-job" style={{ width: `${jobPct}%` }} />
                          </div>
                        </div>
                        <span className="ub-bar-val">{fmt(m.total)}</span>
                      </div>
                    );
                  })}
                  <div className="ub-legend">
                    <span className="ub-legend-dot ub-legend-job" /> Job runs
                    <span className="ub-legend-dot ub-legend-depl" style={{ marginLeft: "1rem" }} /> Deployments
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default UsageBreakdown;
